import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Building2, ClipboardCheck, CheckCircle2, ChevronRight, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

const ROLE_LABEL = { advisor: "Treuhänder", approver: "Freigeber" };

function MandateCard({ m, onOpen }) {
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid={`trustee-mandate-${m.company_id}`}>
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
          {(m.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{m.name}</h3>
            <Badge variant="outline" className="text-[10px]">{ROLE_LABEL[m.role] || m.role}</Badge>
            {m.vat_registered && <Badge variant="outline" className="text-[10px]">MWST</Badge>}
          </div>
          <div className="text-xs text-slate-500">{[m.legal_form, m.city].filter(Boolean).join(" · ") || "—"}</div>
        </div>
        <div className={`text-right ${m.open_checks ? "text-amber-700" : "text-emerald-700"}`} data-testid={`trustee-open-${m.company_id}`}>
          <div className="text-2xl font-semibold leading-none">{m.open_checks}</div>
          <div className="text-[10px] uppercase tracking-wide">offen</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {m.checks.length === 0 && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Keine offenen Prüfungen</div>
        )}
        {m.checks.map((c, i) => (
          <button key={i} type="button" onClick={() => onOpen(m.company_id, c.url)}
            className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ${c.severity === "critical" ? "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-900" : "border-slate-200 hover:bg-slate-50 text-slate-800"}`}
            data-testid={`trustee-check-${m.company_id}-${c.kind}`}>
            {c.severity === "critical" ? <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> : <ClipboardCheck className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
            <span className="font-medium">{c.label}</span>
            <span className="text-slate-500 truncate">{c.detail}</span>
            <ChevronRight className="h-3.5 w-3.5 ml-auto shrink-0 text-slate-400" />
          </button>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => onOpen(m.company_id, "/")} data-testid={`trustee-open-company-${m.company_id}`}>
          Mandant öffnen
        </Button>
      </div>
    </Card>
  );
}

export default function TrusteeCockpit() {
  const navigate = useNavigate();
  const { retryLoad } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData((await apiClient.get("/my/trustee/overview")).data); } catch { setData({ mandates: [], open_checks_total: 0 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openIn = (companyId, url) => {
    localStorage.setItem("demoCompanyId", companyId);
    retryLoad();
    navigate(url);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="trustee-cockpit-page">
      <div className="flex items-start gap-3 mb-6">
        <Briefcase className="h-6 w-6 text-slate-700" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900">Treuhänder-Portal</h1>
          <p className="text-xs text-slate-500">Alle Ihnen zugeteilten Mandanten mit den offenen Prüfungen: Jahresabschluss-Freigaben, Steuerunterlagen, Zahlungs- und Spesenfreigaben, Belege.</p>
        </div>
        <Link to="/cockpit" className="text-xs text-indigo-600 hover:underline self-center" data-testid="trustee-kpi-link">Kennzahlen-Cockpit →</Link>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="trustee-refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <Card className="p-4 border border-slate-200"><div className="text-[11px] uppercase text-slate-500">Mandanten</div><div className="text-2xl font-semibold" data-testid="trustee-mandates-count">{data.mandates_count ?? data.mandates.length}</div></Card>
          <Card className="p-4 border border-slate-200"><div className="text-[11px] uppercase text-slate-500">Offene Prüfungen</div><div className={`text-2xl font-semibold ${data.open_checks_total ? "text-amber-700" : "text-emerald-700"}`} data-testid="trustee-open-total">{data.open_checks_total}</div></Card>
          <Card className="p-4 border border-slate-200 hidden sm:block"><div className="text-[11px] uppercase text-slate-500">Angemeldet als</div><div className="text-sm font-medium truncate">{data.trustee_email}</div></Card>
        </div>
      )}

      {data && data.mandates.length === 0 && !loading && (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="trustee-empty">
          <Building2 className="h-6 w-6 mx-auto mb-2 text-slate-400" />
          Ihnen ist noch kein Mandant zugeteilt. Der Firmeninhaber kann Sie unter «Freigaben &amp; Zugriff» als Treuhänder einladen.
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {(data?.mandates || []).map((m) => <MandateCard key={m.company_id} m={m} onOpen={openIn} />)}
      </div>
    </div>
  );
}
